import React, { useState, useEffect } from 'react';
import ReusableTable from '../components/adminTable';
import { TableColumn, TableAction } from '../components/adminTable';
import { Modal, ConfirmModal, Button, FormGroup, FormLabel, FormControl, FormSelect, MultiSelect } from '../components/adminModals';
import { menuService, MenuItem, RoleItem, RoleMenuItem, AssignMenuPayload } from '../services/adminAuth';
import { useNotification } from '../../sharedComponents/Notifications';
import { Plus } from 'lucide-react';
import { MENU_GROUPS } from '../layout/Sidebar';
import Pagination from '../components/Pagination';

function ManageMenu() {
    const { showNotification } = useNotification();
    const [menuData, setMenuData] = useState<MenuItem[]>([]);
    const [roleData, setRoleData] = useState<RoleItem[]>([]);
    const [activeTab, setActiveTab] = useState<'menu' | 'role'>('menu');
    const [loading, setLoading] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isToggleStatusModalOpen, setIsToggleStatusModalOpen] = useState(false);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [isDeleteRoleMenuOpen, setIsDeleteRoleMenuOpen] = useState(false);
    const [selectedMenu, setSelectedMenu] = useState<MenuItem | null>(null);
    const [selectedRole, setSelectedRole] = useState<RoleItem | null>(null);
    const [menuToToggle, setMenuToToggle] = useState<MenuItem | null>(null);
    const [currentRoleMenus, setCurrentRoleMenus] = useState<RoleMenuItem[]>([]);
    const [availableMenusForAssign, setAvailableMenusForAssign] = useState<MenuItem[]>([]);
    const [formData, setFormData] = useState({ displayName: '', route: '', group: '' });
    const [assignData, setAssignData] = useState({ menuIds: [] as number[] });
    const [deleteRoleMenuData, setDeleteRoleMenuData] = useState({ roleMenuId: '' });
    const [submitting, setSubmitting] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);

    // Menu tab pagination
    const [menuCurrentPage, setMenuCurrentPage] = useState(1);
    const [menuPageSize, setMenuPageSize] = useState(10);
    const [menuTotalRecords, setMenuTotalRecords] = useState(0);
    const [menuTotalPages, setMenuTotalPages] = useState(0);

    // Menu tab filter — single field, instant-apply
    const [menuStatusFilter, setMenuStatusFilter] = useState<string>('');
    const [menuFilterOpen, setMenuFilterOpen] = useState(false);

    // Role tab pagination
    const [roleCurrentPage, setRoleCurrentPage] = useState(1);
    const [rolePageSize, setRolePageSize] = useState(10);
    const [roleTotalRecords, setRoleTotalRecords] = useState(0);
    const [roleTotalPages, setRoleTotalPages] = useState(0);

    const sortedMenuGroups = [...MENU_GROUPS].sort((a, b) => a.localeCompare(b));

    // ── Re-fetch menus on filter/page changes ──────────────────────────────────
    useEffect(() => {
        if (activeTab === 'menu') fetchMenus();
    }, [menuCurrentPage, menuPageSize, menuStatusFilter]);

    // ── Re-fetch roles on page changes ────────────────────────────────────────
    useEffect(() => {
        if (activeTab === 'role') fetchRoles();
    }, [roleCurrentPage, rolePageSize]);

    // ── Tab-switch triggers an immediate fetch ─────────────────────────────────
    useEffect(() => {
        if (activeTab === 'menu') fetchMenus();
        else fetchRoles();
    }, [activeTab]);

    // ──────────────────────────────────────────────────────────────────────────

    const fetchMenus = async () => {
        setLoading(true);
        try {
            const statusArg = menuStatusFilter === 'true' ? true
                : menuStatusFilter === 'false' ? false
                : undefined;

            const response = await menuService.getAllMenu(statusArg);
            if (response.isSuccess) {
                const allData = response.data;
                setMenuTotalRecords(allData.length);
                setMenuTotalPages(Math.ceil(allData.length / menuPageSize));
                const start = (menuCurrentPage - 1) * menuPageSize;
                setMenuData(allData.slice(start, start + menuPageSize));
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to fetch menus' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while fetching menus' });
            } finally {
            setLoading(false);
        }
    };

    const fetchRoles = async () => {
        setLoading(true);
        try {
            const response = await menuService.getAdminRoles();
            if (response.isSuccess) {
                const filtered = response.data.filter(r => r.name !== 'Author');
                setRoleTotalRecords(filtered.length);
                setRoleTotalPages(Math.ceil(filtered.length / rolePageSize));
                const start = (roleCurrentPage - 1) * rolePageSize;
                setRoleData(filtered.slice(start, start + rolePageSize));
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to fetch roles' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while fetching roles' });
            } finally {
            setLoading(false);
        }
    };

    const fetchMenuByRoleId = async (roleId: number, status?: boolean) => {
        try {
            const response = await menuService.getMenuByRoleId(roleId, status);
            if (response.isSuccess) {
                setCurrentRoleMenus(response.data);
                return response.data;
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to fetch role menus' });
                return [];
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while fetching role menus' });
            return [];
        }
    };

    const fetchAllActiveMenus = async () => {
        try {
            const response = await menuService.getAllMenu(true);
            return response.isSuccess ? response.data : [];
        } catch {
            return [];
        }
    };

    // ── Filter handlers ────────────────────────────────────────────────────────

    const handleMenuStatusFilterChange = (value: string) => {
        setMenuStatusFilter(value);
        setMenuCurrentPage(1);
    };

    const handleClearMenuFilter = () => {
        setMenuStatusFilter('');
        setMenuCurrentPage(1);
    };

    // ── Menu CRUD ──────────────────────────────────────────────────────────────

    const handleSearch = (query: string) => {
        };

    const handleEdit = (menu: MenuItem) => {
        setSelectedMenu(menu);
        const groupValue = MENU_GROUPS.includes(menu.group as any) ? menu.group : '';
        setFormData({ displayName: menu.displayName, route: menu.route, group: groupValue });
        setIsEditModalOpen(true);
    };

    const handleToggleStatusClick = (menu: MenuItem) => {
        setMenuToToggle(menu);
        setIsToggleStatusModalOpen(true);
    };

    const handleToggleStatusConfirm = async () => {
        if (!menuToToggle) return;
        setSubmitting(true);
        try {
            const response = await menuService.updateMenu({
                id: menuToToggle.id,
                displayName: menuToToggle.displayName,
                route: menuToToggle.route,
                group: menuToToggle.group,
                isActive: !menuToToggle.active
            });
            if (response.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: `Menu ${!menuToToggle.active ? 'activated' : 'deactivated'} successfully`,
                    timer: 3000
                });
                setIsToggleStatusModalOpen(false);
                setMenuToToggle(null);
                await fetchMenus();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to update menu status' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while updating menu status' });
            } finally {
            setSubmitting(false);
        }
    };

    const handleAddMenu = async () => {
        if (!formData.displayName || !formData.route || !formData.group) {
            showNotification('warning', { title: 'Validation Error', message: 'Please fill in all fields' });
            return;
        }
        setSubmitting(true);
        try {
            const response = await menuService.createMenu({
                id: 0,
                displayName: formData.displayName,
                route: formData.route,
                group: formData.group,
                isActive: true
            });
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Menu created successfully', timer: 3000 });
                setIsAddModalOpen(false);
                setFormData({ displayName: '', route: '', group: '' });
                await fetchMenus();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to create menu' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while creating the menu' });
            } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateMenu = async () => {
        if (!selectedMenu || !formData.displayName || !formData.route || !formData.group) {
            showNotification('warning', { title: 'Validation Error', message: 'Please fill in all fields' });
            return;
        }
        setSubmitting(true);
        try {
            const response = await menuService.updateMenu({
                id: selectedMenu.id,
                displayName: formData.displayName,
                route: formData.route,
                group: formData.group,
                isActive: selectedMenu.active
            });
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Menu updated successfully', timer: 3000 });
                setIsEditModalOpen(false);
                setFormData({ displayName: '', route: '', group: '' });
                setSelectedMenu(null);
                await fetchMenus();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to update menu' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while updating the menu' });
            } finally {
            setSubmitting(false);
        }
    };

    // ── Role menu assign / delete ──────────────────────────────────────────────

    const handleOpenAssignModal = async (role: RoleItem) => {
        setSelectedRole(role);
        setIsAssignModalOpen(true);
        setModalLoading(true);
        try {
            const [roleMenus, allActiveMenus] = await Promise.all([
                fetchMenuByRoleId(role.id, true),
                fetchAllActiveMenus()
            ]);
            const assignedIds = roleMenus.map((rm: RoleMenuItem) => rm.menuId);
            setAvailableMenusForAssign(allActiveMenus.filter(m => !assignedIds.includes(m.id)));
            setAssignData({ menuIds: [] });
        } finally {
            setModalLoading(false);
        }
    };

    const handleAssignMenu = async () => {
        if (!selectedRole || assignData.menuIds.length === 0) {
            showNotification('warning', { title: 'Validation Error', message: 'Please select at least one menu' });
            return;
        }
        setSubmitting(true);
        try {
            const payload: AssignMenuPayload = {
                roleId: selectedRole.id,
                menuIds: [...currentRoleMenus.map(rm => rm.menuId), ...assignData.menuIds]
            };
            const response = await menuService.assignMenuToRole(payload);
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Menu assigned successfully!', timer: 3000 });
                setIsAssignModalOpen(false);
                setAssignData({ menuIds: [] });
                setSelectedRole(null);
                setCurrentRoleMenus([]);
                await fetchRoles();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to assign menu' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while assigning the menu' });
            } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteRoleMenu = async (role: RoleItem) => {
        setSelectedRole(role);
        setIsDeleteRoleMenuOpen(true);
        setModalLoading(true);
        try {
            await fetchMenuByRoleId(role.id, true);
            setDeleteRoleMenuData({ roleMenuId: '' });
        } finally {
            setModalLoading(false);
        }
    };

    const handleConfirmDeleteRoleMenu = async () => {
        if (!deleteRoleMenuData.roleMenuId) {
            showNotification('warning', { title: 'Validation Error', message: 'Please select a menu to delete' });
            return;
        }
        setSubmitting(true);
        try {
            const response = await menuService.deleteMenuFromRole(parseInt(deleteRoleMenuData.roleMenuId));
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Menu removed from role successfully!', timer: 3000 });
                setIsDeleteRoleMenuOpen(false);
                setDeleteRoleMenuData({ roleMenuId: '' });
                setSelectedRole(null);
                setCurrentRoleMenus([]);
                await fetchRoles();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to delete menu from role' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while deleting menu from role' });
            } finally {
            setSubmitting(false);
        }
    };

    // ── Helpers ────────────────────────────────────────────────────────────────

    const getSelectedMenuNames = () =>
        availableMenusForAssign
            .filter(menu => assignData.menuIds.includes(menu.id))
            .map(menu => menu.displayName)
            .join(', ');

    const getCurrentMenuNames = () =>
        currentRoleMenus.map(rm => rm.menu.displayName).join(', ');

    const activeCurrentRoleMenus = currentRoleMenus.filter(rm => rm.menu.active);

    const Spinner = () => (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );

    const ModalLoader = ({ label }: { label: string }) => (
        <div className="py-12 flex justify-center items-center">
            <div className="flex flex-col items-center gap-3">
                <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-gray-600">{label}</span>
            </div>
        </div>
    );

    // ── Table columns ──────────────────────────────────────────────────────────

    const menuColumns: TableColumn<MenuItem>[] = [
        {
            key: 'displayName',
            label: 'Display Name',
            render: (value) => <span className="font-medium">{value}</span>
        },
        {
            key: 'group',
            label: 'Group',
            render: (value) => <span className="text-[12px] font-semibold text-[#45556C]">{value}</span>
        },
        {
            key: 'route',
            label: 'Route',
            render: (value) => <span className="text-[#0d6efd]">{value}</span>
        },
        {
            key: 'active',
            label: 'Status',
            render: (value) => (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                    value ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${value ? 'bg-green-500' : 'bg-red-500'}`} />
                    {value ? 'Active' : 'Inactive'}
                </span>
            )
        },
    ];

    const menuActions: TableAction<MenuItem>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                { label: 'Edit', onClick: handleEdit, className: 'text-blue-600' },
                {
                    label: (row: MenuItem) => row.active ? 'Deactivate' : 'Activate',
                    onClick: handleToggleStatusClick,
                    className: (row: MenuItem) => row.active ? 'text-red-600' : 'text-green-600'
                }
            ],
            title: 'Actions'
        }
    ];

    const roleColumns: TableColumn<RoleItem>[] = [
        {
            key: 'name',
            label: 'Role Name',
            render: (value) => <span className="font-medium">{value}</span>
        },
        {
            key: 'active',
            label: 'Status',
            render: (value) => (
                <span className={`inline-block py-[0.25em] px-[0.4em] text-[0.75em] font-bold leading-[1] whitespace-nowrap ${
                    value ? 'text-green-600' : 'text-red-600'
                }`}>
                    {value ? 'Active' : 'Inactive'}
                </span>
            )
        }
    ];

    const roleActions: TableAction<RoleItem>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                { label: 'Assign Menu', onClick: handleOpenAssignModal, className: 'text-blue-600' },
                { label: 'Delete Menu', onClick: handleDeleteRoleMenu, className: 'text-red-600' }
            ],
            title: 'Actions'
        }
    ];

    // ── Filter panel ───────────────────────────────────────────────────────────

    const menuFilterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex flex-wrap gap-4 items-end">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                        value={menuStatusFilter}
                        onChange={(e) => handleMenuStatusFilterChange(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                    </select>
                </div>
            </div>
            <button onClick={handleClearMenuFilter} className="mt-4 text-sm text-blue-600 hover:underline">
                Clear all filters
            </button>
        </div>
    );

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="w-full">
            <div className="flex flex-wrap -mx-[15px]">
                <div className="w-full px-[15px]">
                    {/* Header */}
                    <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Manage Menu</h1>
                            <p className="text-gray-500 text-sm sm:text-base">Create and manage your application menus</p>
                        </div>

                        {activeTab === 'menu' && (
                            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                                <Button
                                    variant="success"
                                    onClick={() => setIsAddModalOpen(true)}
                                    className="w-full sm:w-auto flex items-center justify-center gap-2"
                                >
                                    <Plus size={16} />
                                    Add New Menu
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Tabs */}
                    <div className="mb-6">
                        <div className="bg-white rounded-xl border border-gray-200 flex">
                            <button
                                onClick={() => setActiveTab('menu')}
                                className={`flex-1 px-6 py-3 font-medium rounded-l-md transition-colors ${
                                    activeTab === 'menu' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                Menu Management
                            </button>
                            <button
                                onClick={() => setActiveTab('role')}
                                className={`flex-1 px-6 py-3 font-medium transition-colors ${
                                    activeTab === 'role' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                Role Access Management
                            </button>
                        </div>
                    </div>

                    {/* Menu Management Tab */}
                    {activeTab === 'menu' && (
                        <>
                            <ReusableTable
                                title="Menu Management"
                                columns={menuColumns}
                                data={menuData}
                                actions={menuActions}
                                loading={loading}
                                searchPlaceholder="Search menus..."
                                onSearch={handleSearch}
                                emptyMessage="No menus found. Create your first menu!"
                                showSearch={true}
                                showFilter={true}
                                filterPanel={menuFilterPanel}
                                onFilterToggle={() => setMenuFilterOpen(!menuFilterOpen)}
                                isFilterOpen={menuFilterOpen}
                            />
                            {!loading && menuTotalRecords > 0 && (
                                <Pagination
                                    currentPage={menuCurrentPage}
                                    totalPages={menuTotalPages}
                                    totalRecords={menuTotalRecords}
                                    pageSize={menuPageSize}
                                    onPageChange={setMenuCurrentPage}
                                    onPageSizeChange={(size) => { setMenuPageSize(size); setMenuCurrentPage(1); }}
                                />
                            )}
                        </>
                    )}

                    {/* Role Access Management Tab */}
                    {activeTab === 'role' && (
                        <>
                            <ReusableTable
                                title="Role Access Management"
                                columns={roleColumns}
                                data={roleData}
                                actions={roleActions}
                                loading={loading}
                                searchPlaceholder="Search roles..."
                                onSearch={handleSearch}
                                emptyMessage="No roles found."
                                showSearch={true}
                                showFilter={false}
                            />
                            {!loading && roleTotalRecords > 0 && (
                                <Pagination
                                    currentPage={roleCurrentPage}
                                    totalPages={roleTotalPages}
                                    totalRecords={roleTotalRecords}
                                    pageSize={rolePageSize}
                                    onPageChange={setRoleCurrentPage}
                                    onPageSizeChange={(size) => { setRolePageSize(size); setRoleCurrentPage(1); }}
                                />
                            )}
                        </>
                    )}

                    {/* Add Menu Modal */}
                    <Modal
                        isOpen={isAddModalOpen}
                        onClose={() => { setIsAddModalOpen(false); setFormData({ displayName: '', route: '', group: '' }); }}
                        title="Add New Menu"
                        description="Create a new menu item for the application"
                    >
                        <div>
                            <FormGroup>
                                <FormLabel required>Display Name</FormLabel>
                                <FormControl type="text" value={formData.displayName} onChange={(e) => setFormData({ ...formData, displayName: e.target.value })} placeholder="Enter display name" disabled={submitting} required />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel required>Route</FormLabel>
                                <FormControl type="text" value={formData.route} onChange={(e) => setFormData({ ...formData, route: e.target.value })} placeholder="example-route (e.g., admin/dashboard)" disabled={submitting} required />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel required>Group</FormLabel>
                                <FormSelect value={formData.group} onChange={(e) => setFormData({ ...formData, group: e.target.value })} disabled={submitting}>
                                    <option value="">Select a group</option>
                                    {sortedMenuGroups.map((group) => <option key={group} value={group}>{group}</option>)}
                                </FormSelect>
                            </FormGroup>
                            <div className="flex gap-3 mt-6">
                                <Button variant="white" onClick={() => { setIsAddModalOpen(false); setFormData({ displayName: '', route: '', group: '' }); }} className="flex-1" disabled={submitting}>Cancel</Button>
                                <Button variant="success" onClick={handleAddMenu} className="flex-1" disabled={submitting}>
                                    {submitting ? <span className="flex items-center justify-center gap-2"><Spinner /> Adding...</span> : 'Add Menu'}
                                </Button>
                            </div>
                        </div>
                    </Modal>

                    {/* Edit Menu Modal */}
                    <Modal
                        isOpen={isEditModalOpen}
                        onClose={() => { setIsEditModalOpen(false); setFormData({ displayName: '', route: '', group: '' }); setSelectedMenu(null); }}
                        title="Edit Menu"
                        description="Update menu item details"
                    >
                        <div>
                            <FormGroup>
                                <FormLabel required>Display Name</FormLabel>
                                <FormControl type="text" value={formData.displayName} onChange={(e) => setFormData({ ...formData, displayName: e.target.value })} disabled={submitting} required />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel required>Route</FormLabel>
                                <FormControl type="text" value={formData.route} onChange={(e) => setFormData({ ...formData, route: e.target.value })} disabled={submitting} required />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel required>Group</FormLabel>
                                <FormSelect value={formData.group} onChange={(e) => setFormData({ ...formData, group: e.target.value })} disabled={submitting}>
                                    <option value="">Select a group</option>
                                    {sortedMenuGroups.map((group) => <option key={group} value={group}>{group}</option>)}
                                </FormSelect>
                            </FormGroup>
                            <div className="flex gap-3 mt-6">
                                <Button variant="white" onClick={() => { setIsEditModalOpen(false); setFormData({ displayName: '', route: '', group: '' }); setSelectedMenu(null); }} className="flex-1" disabled={submitting}>Cancel</Button>
                                <Button variant="success" onClick={handleUpdateMenu} className="flex-1" disabled={submitting}>
                                    {submitting ? <span className="flex items-center justify-center gap-2"><Spinner /> Updating...</span> : 'Update Menu'}
                                </Button>
                            </div>
                        </div>
                    </Modal>

                    {/* Toggle Status Confirmation Modal */}
                    <ConfirmModal
                        isOpen={isToggleStatusModalOpen}
                        onClose={() => { setIsToggleStatusModalOpen(false); setMenuToToggle(null); }}
                        onConfirm={handleToggleStatusConfirm}
                        title={menuToToggle?.active ? 'Deactivate Menu' : 'Activate Menu'}
                        message={menuToToggle?.active
                            ? `Are you sure you want to deactivate "${menuToToggle?.displayName}"? It will no longer be accessible.`
                            : `Are you sure you want to activate "${menuToToggle?.displayName}"?`}
                        confirmText={menuToToggle?.active ? 'Deactivate' : 'Activate'}
                        cancelText="Cancel"
                        variant={menuToToggle?.active ? 'danger' : 'info'}
                        isSubmitting={submitting}
                    />

                    {/* Assign Menu Modal */}
                    <Modal
                        isOpen={isAssignModalOpen}
                        onClose={() => { setIsAssignModalOpen(false); setAssignData({ menuIds: [] }); setSelectedRole(null); setCurrentRoleMenus([]); setAvailableMenusForAssign([]); setModalLoading(false); }}
                        title="Assign Menu to Role"
                        description="Grant menu access to this role"
                        maxWidth="lg"
                    >
                        <div>
                            {modalLoading ? <ModalLoader label="Loading role menus..." /> : (
                                <>
                                    <FormGroup>
                                        <FormLabel>Current Role</FormLabel>
                                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                            <span className="font-medium text-gray-900">{selectedRole?.name}</span>
                                        </div>
                                    </FormGroup>
                                    <FormGroup>
                                        <FormLabel>Current Assigned Menus</FormLabel>
                                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                            <span className="text-sm text-gray-700">{getCurrentMenuNames() || 'No menus assigned'}</span>
                                        </div>
                                    </FormGroup>
                                    <FormGroup>
                                        <FormLabel required>Add New Menus</FormLabel>
                                        <MultiSelect
                                            options={availableMenusForAssign.map(menu => ({ value: menu.id, label: menu.displayName }))}
                                            selectedValues={assignData.menuIds}
                                            onChange={(selected) => setAssignData({ menuIds: selected as number[] })}
                                            placeholder="Select additional menus..."
                                            disabled={submitting}
                                        />
                                        {assignData.menuIds.length > 0 && (
                                            <div className="mt-2 p-2 bg-green-50 rounded text-sm text-gray-600">
                                                <strong>New Menus:</strong> {getSelectedMenuNames()}
                                            </div>
                                        )}
                                    </FormGroup>
                                    <div className="flex gap-3 mt-6">
                                        <Button variant="white" onClick={() => { setIsAssignModalOpen(false); setAssignData({ menuIds: [] }); setSelectedRole(null); setCurrentRoleMenus([]); setAvailableMenusForAssign([]); }} className="flex-1" disabled={submitting}>Cancel</Button>
                                        <Button variant="success" onClick={handleAssignMenu} className="flex-1" disabled={submitting}>
                                            {submitting ? <span className="flex items-center justify-center gap-2"><Spinner /> Assigning...</span> : 'Assign Menu'}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </Modal>

                    {/* Delete Role Menu Modal */}
                    <Modal
                        isOpen={isDeleteRoleMenuOpen}
                        onClose={() => { setIsDeleteRoleMenuOpen(false); setDeleteRoleMenuData({ roleMenuId: '' }); setSelectedRole(null); setCurrentRoleMenus([]); setModalLoading(false); }}
                        title="Delete Menu from Role"
                        description="Remove an active menu from this role"
                    >
                        <div>
                            {modalLoading ? <ModalLoader label="Loading role menus..." /> : (
                                <>
                                    <FormGroup>
                                        <FormLabel>Role</FormLabel>
                                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                            <span className="font-medium text-gray-900">{selectedRole?.name}</span>
                                        </div>
                                    </FormGroup>
                                    <FormGroup>
                                        <FormLabel required>Select Menu to Delete</FormLabel>
                                        <FormSelect value={deleteRoleMenuData.roleMenuId} onChange={(e) => setDeleteRoleMenuData({ roleMenuId: e.target.value })} disabled={submitting}>
                                            <option value="">Choose a menu to remove</option>
                                            {activeCurrentRoleMenus.map((roleMenu) => (
                                                <option key={roleMenu.id} value={roleMenu.id}>{roleMenu.menu.displayName}</option>
                                            ))}
                                        </FormSelect>
                                    </FormGroup>
                                    <div className="flex gap-3 mt-6">
                                        <Button variant="white" onClick={() => { setIsDeleteRoleMenuOpen(false); setDeleteRoleMenuData({ roleMenuId: '' }); setSelectedRole(null); setCurrentRoleMenus([]); }} className="flex-1" disabled={submitting}>Cancel</Button>
                                        <Button variant="danger" onClick={handleConfirmDeleteRoleMenu} className="flex-1" disabled={submitting}>
                                            {submitting ? <span className="flex items-center justify-center gap-2"><Spinner /> Deleting...</span> : 'Delete Menu'}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </Modal>
                </div>
            </div>
        </div>
    );
}

export default ManageMenu;